import { useEffect, useCallback } from 'react';
import { chain } from 'underscore';
import { useSharedState } from '@trello/shared-state';
import { memberId } from '@trello/session-cookie';
import { ProductFeatures } from '@trello/product-features';
// eslint-disable-next-line no-restricted-imports
import {
  initialState,
  createMenuState,
} from 'app/gamma/src/modules/state/ui/create-menu';
import { workspaceState } from '@trello/workspaces';
import {
  freeBoardsRemaining,
  isPossiblyValidOrgName,
} from '@trello/organizations';
import { isPaidManagedEnterpriseMember } from '@trello/members';
import { usePreSelectWorkspaceQuery } from './PreSelectWorkspaceQuery.generated';

export const usePreSelectWorkspace = (preSelectedWorkspaceId?: string) => {
  const [menuState, setMenuState] = useSharedState(createMenuState);
  const [workspaceStateValue] = useSharedState(workspaceState);
  const { data } = usePreSelectWorkspaceQuery({
    variables: { memberId: memberId || 'me' },
  });
  const isPaidManagedEntMember = isPaidManagedEnterpriseMember({
    confirmed: data?.member?.confirmed,
    idEnterprise: data?.member?.idEnterprise,
    enterpriseLicenses: data?.member?.enterpriseLicenses,
  });

  const isTeamValidForPreSelect = (teamId: string) => {
    const team = data?.member?.organizations.find((org) => org.id === teamId);
    if (team) {
      return (
        !isPaidManagedEntMember ||
        team.products.some(ProductFeatures.isEnterpriseProduct)
      );
    }
    return false;
  };

  const extractTeamFromPathname = () => {
    const pathname = window.location.pathname;
    const match = pathname.match(/\/([^/]+)\/?([^/]+)?/);
    if (!match) {
      return null;
    }
    const orgName = match[1];
    if (!isPossiblyValidOrgName(orgName)) {
      return null;
    }

    const team = data?.member?.organizations.find(
      (org) => org.name === orgName,
    );
    if (team) {
      if (
        !isPaidManagedEntMember ||
        team.products.some(ProductFeatures.isEnterpriseProduct)
      ) {
        return team.id;
      }
    }
    return null;
  };

  const getTeamClosestToBoardLimit = () => {
    const teams = data?.member?.organizations;
    if (!teams) {
      return null;
    }
    const bestTeam = chain(teams)
      .filter((team) => freeBoardsRemaining(team) !== undefined)
      .sortBy((team) => freeBoardsRemaining(team))
      .first()
      .value();
    if (bestTeam) {
      if (
        !isPaidManagedEntMember ||
        bestTeam.products.some(ProductFeatures.isEnterpriseProduct)
      ) {
        return bestTeam.id;
      }
    }
    return null;
  };

  const getSomeValidTeam = () => {
    const teams = data?.member?.organizations;
    if (!teams) {
      return null;
    }
    for (const team of teams) {
      if (
        !isPaidManagedEntMember ||
        team.products.some(ProductFeatures.isEnterpriseProduct)
      ) {
        return team.id;
      }
    }
    return null;
  };

  const resetState = useCallback(() => {
    setMenuState({
      ...initialState,
      selectedTeamId: null,
    });
  }, [setMenuState]);

  useEffect(() => {
    // We choose the selected team ID based on the following priority:
    // 1. If an id is passed in as a prop
    // 2. If our workspace state is defined
    // 3. If a workspace is defined in the url
    // 4. The team closest to its board limit
    // 5. Any team
    if (
      preSelectedWorkspaceId &&
      isTeamValidForPreSelect(preSelectedWorkspaceId)
    ) {
      setMenuState({
        ...menuState,
        selectedTeamId: preSelectedWorkspaceId,
      });
    } else if (
      workspaceStateValue.idWorkspace &&
      isTeamValidForPreSelect(workspaceStateValue.idWorkspace)
    ) {
      setMenuState({
        ...menuState,
        selectedTeamId: workspaceStateValue.idWorkspace,
      });
    } else {
      const bestEffortTeamId =
        extractTeamFromPathname() ||
        getTeamClosestToBoardLimit() ||
        getSomeValidTeam();
      if (bestEffortTeamId) {
        setMenuState({
          ...menuState,
          selectedTeamId: bestEffortTeamId,
        });
      }
    }
    return () => {
      resetState();
    };
    // eslint-disable-next-line @trello/exhaustive-deps
  }, [data, preSelectedWorkspaceId]);
};
